import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { ErrorBanner } from "@/components/ErrorBanner";
import { PageHeader } from "@/components/PageHeader";
import { RequirePermission } from "@/components/RequirePermission";
import { ErrorState, LoadingState } from "@/components/States";
import { useAuth } from "@/lib/AuthContext";
import { adminRolesApi } from "@/lib/api";
import { hasPermission } from "@/lib/auth";

export default function RolesList() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const canManage = hasPermission(user, "role.manage");
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ["admin", "roles"],
    queryFn: () => adminRolesApi.listRoles(),
  });
  const remove = useMutation({
    mutationFn: (role: string) => adminRolesApi.deleteRole(role),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin", "roles"] }),
  });

  return (
    <RequirePermission codes={["role.manage", "user.manage"]}>
      <PageHeader
        eyebrow="admin · roles"
        title={t("roles.title")}
        description={t("roles.description")}
      />
      {canManage && <CreateRoleForm />}
      {remove.isError && (
        <ErrorBanner onDismiss={() => remove.reset()}>{(remove.error as Error).message}</ErrorBanner>
      )}
      {q.isLoading ? (
        <LoadingState />
      ) : q.isError ? (
        <ErrorState error={q.error} />
      ) : (
        <div className="card overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-surface-alt">
              <tr className="text-left text-text-muted font-mono text-xs uppercase">
                <th className="px-4 py-3">{t("roles.colRole")}</th>
                <th className="px-4 py-3">{t("roles.colUsers")}</th>
                <th className="px-4 py-3">{t("roles.colPermissions")}</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {q.data!.map((r) => (
                <tr key={r.role} className="border-t border-border hover:bg-surface-hover">
                  <td className="px-4 py-3">
                    <div className="text-text-primary">{r.name}</div>
                    <div className="font-mono text-xs text-accent-strong">
                      {r.role}
                      {r.is_system && <span className="pill text-xs ml-2">{t("roles.system")}</span>}
                    </div>
                  </td>
                  <td className="px-4 py-3 tabular-nums">{r.user_count}</td>
                  <td className="px-4 py-3">
                    <div className="flex flex-wrap gap-1">
                      {r.permissions.length === 0 ? (
                        <span className="text-text-muted text-xs">—</span>
                      ) : (
                        r.permissions.map((p) => (
                          <span key={p} className="pill text-xs font-mono">{p}</span>
                        ))
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex gap-1 justify-end">
                      <Link className="btn text-xs" to={`/admin/roles/${r.role}`}>
                        {t("common.edit")}
                      </Link>
                      {canManage && !r.is_system && (
                        <button
                          className="btn text-xs"
                          disabled={remove.isPending}
                          onClick={() => {
                            if (window.confirm(t("roles.confirmDelete", { role: r.name }))) {
                              remove.mutate(r.role);
                            }
                          }}
                        >
                          {t("common.delete")}
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </RequirePermission>
  );
}

function CreateRoleForm() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [code, setCode] = useState("");
  const [name, setName] = useState("");
  const create = useMutation({
    mutationFn: () => adminRolesApi.createRole({ code: code.trim().toUpperCase(), name: name.trim() }),
    onSuccess: () => {
      setCode("");
      setName("");
      qc.invalidateQueries({ queryKey: ["admin", "roles"] });
    },
  });
  return (
    <form
      className="card p-4 mb-4 flex flex-wrap gap-3 items-end"
      onSubmit={(e) => {
        e.preventDefault();
        create.mutate();
      }}
    >
      <label className="block text-sm">
        <span className="text-xs font-mono uppercase tracking-wider text-text-muted">
          {t("roles.newCode")}
        </span>
        <input
          className="mt-1 px-3 py-2 rounded-md bg-surface border border-border font-mono"
          value={code}
          onChange={(e) => setCode(e.target.value)}
          placeholder="PACKER"
          required
        />
      </label>
      <label className="block text-sm flex-1 min-w-[14rem]">
        <span className="text-xs font-mono uppercase tracking-wider text-text-muted">
          {t("roles.newName")}
        </span>
        <input
          className="mt-1 w-full px-3 py-2 rounded-md bg-surface border border-border"
          value={name}
          onChange={(e) => setName(e.target.value)}
          required
        />
      </label>
      <button className="btn btn-primary" type="submit" disabled={create.isPending}>
        {t("roles.create")}
      </button>
      {create.isError && (
        <ErrorBanner onDismiss={() => create.reset()}>{(create.error as Error).message}</ErrorBanner>
      )}
    </form>
  );
}
